import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import 'react-quill/dist/quill.snow.css';
import globalAudioManager from './features/MediaLibrary/GlobalAudioManager';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Onboarding from './pages/Onboarding';
import AdminDashboard from './pages/AdminDashboard';
import LearnerDashboard from './features/learner/LearnerDashboard';
import CatalogView from './features/learner/CatalogView';
import LessonView from './features/learner/LessonView';
import TutorDashboard from './pages/TutorDashboard';
import UnitPreview from './pages/UnitPreview';
//import UnitViewer from './features/learner/UnitViewer';

// Stops all audio on every route change - React Router friendly
function AudioNavigationStopper() {
  const location = useLocation();
  useEffect(() => {
    globalAudioManager.stopAll();
  }, [location.pathname]);
  return null;
}

// Protected Route component
const ProtectedRoute = ({ element, allowedRoles }) => {
  const { currentUser, userRole, loading } = useContext(AuthContext);
  
  if (loading) return <div>Loading...</div>;
  
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  
  if (!allowedRoles.includes(userRole)) {
      if (userRole === 'admin') {
        return <Navigate to="/admin" />;
      } else if (userRole === 'official') {
        return <Navigate to="/admin" />; // officials also land on /admin — AdminDashboard shows them a restricted view
      } else if (userRole === 'learner') {
        return <Navigate to="/learn/dashboard" />;
      } else if (userRole === 'tutor') {
        return <Navigate to="/tutor-dashboard" />;
      } else {
        return <Navigate to="/login" />; // tutor falls here!
      }
  }
  
  return element;
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <AudioNavigationStopper />
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute
                  element={<Onboarding />}
                  allowedRoles={['learner']}
                />
              }
            />
            
            {/* Admin routes — officials also land here, AdminDashboard shows them a restricted view */}
            <Route 
              path="/admin/*" 
              element={
                <ProtectedRoute 
                  element={<AdminDashboard />} 
                  allowedRoles={['admin', 'official']} 
                />
              } 
            />
            
            {/* Learner routes */}
            <Route 
              path="/learn/dashboard" 
              element={
                <ProtectedRoute 
                  element={<LearnerDashboard />} 
                  allowedRoles={['learner', 'admin']} 
                />
              } 
            />
            
            <Route 
              path="/learn/catalog/:catalogId" 
              element={
                <ProtectedRoute 
                  element={<CatalogView />} 
                  allowedRoles={['learner', 'admin']} 
                />
              } 
            />
            <Route 
              path="/tutor-dashboard" 
              element={
                <ProtectedRoute 
                  element={<TutorDashboard />} 
                  allowedRoles={['tutor', 'admin']} 
                />
              } 
            />
            {/* Tutor preview route - tutors can preview units */}
            <Route 
              path="/preview-lesson/:lessonId" 
              element={
                <ProtectedRoute 
                  element={<UnitPreview />}
                  allowedRoles={['tutor', 'admin']} 
                />
              } 
            />
      
            <Route 
              path="/learn/lesson/:lessonId" 
              element={
                <ProtectedRoute 
                  element={<LessonView />} 
                  allowedRoles={['learner', 'admin']} 
                />
              } 
            />
            
            {/* Default redirect based on role */}
            <Route path="/" element={<RoleBasedRedirect />} />
            
            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

// Component to handle role-based redirects
const RoleBasedRedirect = () => {
  const { currentUser, userRole, loading } = useContext(AuthContext);
  const [checkingProfile, setCheckingProfile] = React.useState(true);
  const [hasProfile, setHasProfile] = React.useState(false);

  React.useEffect(() => {
    if (!currentUser || userRole !== 'learner') {
      setCheckingProfile(false);
      return;
    }
    // Check if learner has completed onboarding
    import('firebase/firestore').then(({ getDoc, doc }) => {
      import('./firebase/config').then(({ db }) => {
        getDoc(doc(db, 'learnerProfiles', currentUser.uid)).then(snap => {
          setHasProfile(snap.exists());
          setCheckingProfile(false);
        });
      });
    });
  }, [currentUser, userRole]);

  if (loading || checkingProfile) return <div>Loading...</div>;

  if (!currentUser) return <Navigate to="/login" />;

  switch (userRole) {
    case 'admin':
      return <Navigate to="/admin" />;
    case 'official':
      return <Navigate to="/admin" />;
    case 'learner':
      return hasProfile
        ? <Navigate to="/learn/dashboard" />
        : <Navigate to="/onboarding" />;
    case 'tutor':
      return <Navigate to="/tutor-dashboard" />;
    default:
      return <Navigate to="/login" />;
  }
};

export default App;